// @ts-nocheck
// no check with TS because this file is cloned from the UDS repo - we are going to use this file from the UDS repo coming soon

import React from 'react';
import { css } from 'twind/css';
import { tw, style } from 'twind/style';
import {
  useResponsiveProp,
  useUdsTranslate,
} from '@global-ecom/nitro-uds/hooks';
import { IconNames } from '@global-ecom/nitro-uds/icons';
import { DotAnimation, Icon, IconProps } from '@global-ecom/nitro-uds/elements';

export const ctaStyle = style({
  base: 'pointer-events-all relative inline-flex whitespace-normal items-center justify-center h-10 min-w-max border-1 text-white bg-neutral-100 border-neutral-100 hover:(bg-neutral-80 border-neutral-80) focus-visible:(bg-neutral-80 border-neutral-80 ring-0 outline-none)',
  variants: {
    /**
     * Choose one of the core CTA styles.
     *
     * - primary: a solid rectangle
     * - secondary: outline rectangle
     * - ghost: transparent rectangle
     * - underline: deprecated - use `ButtonUnderline` or `ButtonText` instead
     *
     * @defaultValue primary
     *
     * @example
     * ```
     * <ButtonCta variant="primary" label="Shop Running" />
     * ```
     */
    variant: {
      primary:
        'text-white bg-neutral-100 border-neutral-100 hover:(bg-neutral-80 border-neutral-80) focus-visible:(bg-neutral-80 border-neutral-80)',
      secondary:
        'text-neutral-100 border-neutral-40 bg-transparent hover:(bg-transparent border-neutral-100) focus-visible:(bg-transparent border-neutral-100)',
      ghost:
        'bg-neutral-100 border-transparent bg-opacity-0 text-neutral-100 hover:(border-transparent bg-opacity-20) focus-visible:(border-transparent bg-opacity-20)',
      underline:
        'h-auto rounded-none border-0 bg-transparent border-transparent text-neutral-100 hover:(bg-transparent) focus-visible:(bg-transparent)',
    },
    /**
     * Use to render elements on dark backgrounds
     *
     * @defaultValue false
     *
     * @example
     * ```
     * <ButtonCta
     *   variant="primary"
     *   label="Shop Running"
     *   invert={true}
     * />
     * ```
     */
    invert: {
      true: 'bg-white border-white text-neutral-100 hover:(bg-neutral-20 border-neutral-20) focus-visible:(bg-neutral-20 border-neutral-20)',
      false: '',
    },
    /**
     * Set rounded corners
     *
     * @defaultValue false
     */
    round: {
      true: 'rounded-full',
      false: 'rounded-sm',
    },
    /**
     * Set to full width
     *
     * @defaultValue false
     */
    bleed: {
      true: 'w-full',
      false: '',
    },
    /**
     * Semantic color property. Currently only supports `brand`
     */
    color: {
      default: '',
      brand:
        'bg-brand-60  border-brand-60 text-white hover:(border-neutral-80 bg-neutral-80) focus-visible:(border-neutral-80 bg-neutral-80)',
    },
    /**
     * Set the element size.
     *
     * @defaultValue base
     */
    size: {
      sm: 'h-8 px-3',
      base: 'h-10 px-4',
      lg: 'h-14 px-6',
    },
    /**
     * The mode of the button
     * - text: renders a text button
     * - icon: renders an icon button
     *
     * @defaultValue text
     *
     * @deprecated use `ButtonIcon` instead for icon buttons
     */
    mode: {
      icon: 'px-0 w-10 h-10 rounded-full',
      text: '',
    },
    dimmed: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
    loading: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
  },
  matches: [
    {
      mode: 'icon',
      size: 'sm',
      use: 'w-8 h-8',
    },
    {
      mode: 'icon',
      size: 'base',
      use: 'w-10 h-10',
    },
    {
      mode: 'icon',
      size: 'lg',
      use: 'w-14 h-14 px-0',
    },
    {
      variant: 'underline',
      use: 'px-0',
    },
    {
      variant: 'underline',
      mode: 'icon',
      size: 'sm',
      use: 'w-4 h-6',
    },
    {
      variant: 'underline',
      mode: 'icon',
      size: 'base',
      use: 'w-6 h-6',
    },
    {
      variant: 'underline',
      mode: 'icon',
      size: 'lg',
      use: 'w-8 h-6',
    },

    {
      variant: 'primary',
      dimmed: true,
      use: 'bg-neutral-30 border-neutral-30 text-neutral-80 hover:(bg-neutral-30 border-neutral-30) focus-visible:(bg-neutral-30 border-neutral-30)',
    },
    {
      variant: 'primary',
      dimmed: true,
      invert: true,
      use: 'bg-neutral-30 border-neutral-30 text-neutral-80 hover:(bg-neutral-30 border-neutral-30) focus-visible:(bg-neutral-30 border-neutral-30)',
    },

    {
      variant: 'secondary',
      invert: true,
      use: 'bg-transparent border-neutral-60 text-white hover:(border-white bg-transparent) focus-visible:(border-white bg-transparent)',
    },
    {
      variant: 'secondary',
      dimmed: true,
      use: 'bg-transparent text-neutral-60 border-current hover:(bg-transparent border-current) focus-visible:(bg-transparent border-current)',
    },
    {
      variant: 'secondary',
      dimmed: true,
      invert: true,
      use: 'bg-transparent text-neutral-50 border-current hover:(bg-transparent border-current) focus-visible:(bg-transparent border-current)',
    },
    {
      variant: 'ghost',
      invert: true,
      use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-10 hover:(border-transparent bg-opacity-20) focus-visible:(border-transparent bg-opacity-20)',
    },
    {
      variant: 'ghost',
      dimmed: true,
      invert: false,
      use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-60 hover:bg-transparent focus-visible:bg-transparent',
    },
    {
      variant: 'ghost',
      dimmed: true,
      invert: true,
      use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-50 hover:bg-transparent focus-visible:bg-transparent',
    },
    {
      color: 'brand',
      invert: true,
      use: 'bg-brand-60 border-brand-60 text-white hover:(border-neutral-80 bg-neutral-80) focus-visible:(border-neutral-80 bg-neutral-80)',
    },
    {
      color: 'brand',
      dimmed: true,
      use: 'bg-neutral-30 border-neutral-30 text-neutral-80 hover:(bg-neutral-30 border-neutral-30) focus-visible:(bg-neutral-30 border-neutral-30)',
    },
    {
      variant: 'underline',
      invert: true,
      use: 'bg-transparent border-none text-white hover:(bg-transparent border-transparent) focus-visible:(bg-transparent border-transparent)',
    },
    {
      variant: 'underline',
      dimmed: true,
      use: 'bg-transparent border-none text-neutral-50 hover:(bg-transparent border-transparent) focus-visible:(bg-transparent border-transparent)',
    },
  ],
  defaults: {
    variant: 'primary',
    size: 'base',
    color: 'default',
    round: false,
  },
});

export type UseActionCtaProps = Parameters<typeof ctaStyle>[0] & {
  /**
   * Render the button as visually disabled, but remains interactive and
   * in the tab order. The `aria-disabled` attribute is applied, so assistive technologies
   * can announce the `dimmed` state, when the user focuses on the button.
   * Used often in combination with te `dimmedReason` property
   *
   * @example
   * ```
   * <ButtonCta
   *   label="Submit Form"
   *   dimmed={true}
   *   dimmedReason="4 errors are found"
   * />
   * ```
   */
  dimmed?: boolean;
  /**
   * When `dimmed`, use the `dimmedReason` to render a tooltip, explaining
   * why the button is `dimmed`
   *
   * @defaultValue false
   */
  dimmedReason?: string;
  /**
   * Render an svg icon from the `@cyber-cats/uds/icons` package, horizontally besides the label
   *
   * @example
   * ```
   * <ButtonCta icon="cart" />
   * ```
   */
  icon?: IconNames;
  /**
   * When `iconEnd` is true, the icon will be rendered to the end of the text orientation.
   * By default and when false, icons render before the text.
   *
   * @defaultValue false
   */
  iconEnd?: boolean;
  /**
   * The text label of the button.
   *
   * @example
   * ```
   * <ButtonCta
   *   label="Shop Running"
   * />
   * ```
   */
  label: string;

  /**
   * Optional screen-reader text label of the button.
   *
   * The `srOnlyLabel` will render visually hidden, and the visual label, will render as aria-hidden. So assistive techologies, will announce the `srOnlyLabel`
   *
   * That is used for situations where a sighted user can infer surrounding context from the UI, but assistive techologies cannot provide the additional context.
   *
   * @example
   * ```
   * <ButtonCta
   *   label="Remove"
   *   srOnlyLabel="Remove product XR from cart"
   * />
   * ```
   */
  srOnlyLabel?: string;
  /**
   * ButtonCta can't have children nodes
   */
  children?: never;
  /**
   * Hides the text, and renders a little animation. Also appends a visually hidden `loading` label
   *
   * @defaultValue false
   */
  loading?: boolean;
  /**
   * Uppercase all the text
   *
   * @defaultValue true
   */
  uppercase?: boolean;
  /**
   * Makes all the text bold
   *
   * @defaultValue true
   */
  bold?: boolean;
  /**
   * Height for the line that goes underline the text when using the variant "underline"
   *
   * @defaultValue 2px
   */
  underlineSizeInPx?: string;
  /**
   * When `mode` is set to `icon`, the button's label becomes visually hidden,
   * and only the icon remains visible.
   *
   * @defaultValue text
   *
   * @example
   *
   * ```
   * <ButtonCta
   *   mode="icon"
   *   icon="heart"
   *   label="Add to wishlist"
   * />
   * ```
   */
  mode?: 'text' | 'icon';
};

const iconSizeMap = {
  sm: 'base',
  base: '2xl',
  lg: '4xl',
} as const;

const getIconSizeByActionSize = (
  size: Parameters<typeof ctaStyle>[0]['size']
): IconProps['size'] => {
  let iconSize: IconProps['size'];

  if (typeof size === 'string') {
    iconSize = iconSizeMap[size];
  } else {
    iconSize = Object.assign({});
    for (const [key, value] of Object.entries(size)) {
      iconSize[key] = iconSizeMap[value];
    }
  }

  return iconSize;
};

export const useActionCta = ({
  className,
  label,
  srOnlyLabel,
  variant = 'primary',
  invert = false,
  round = false,
  bleed = false,
  size = 'base',
  color = 'default',
  mode = 'text',
  dimmed = false,
  dimmedReason,
  icon,
  iconEnd,
  loading,
  uppercase = true,
  bold = true,
  underlineSizeInPx = '2px',
}: UseActionCtaProps) => {
  const t = useUdsTranslate();

  const actionClassName = tw(
    'group',
    ctaStyle({
      variant: variant,
      invert: invert,
      size: size,
      round: round,
      bleed: bleed,
      color: color,
      mode: mode,
      dimmed: dimmed,
      loading: loading,
    }),
    iconEnd ? 'flex-row-reverse' : 'flex-row',
    `after::(absolute hidden pointer-events-none -left-1 -top-1 -right-1 -bottom-1)`,
    `after::(border-2 ${invert ? 'border-outline-invert' : 'border-outline'} ${
      mode === 'icon' || round ? 'rounded-full' : 'rounded-sm'
    } )`,

    variant !== 'underline' && 'focus-visible:after::block',
    css`
      &::after {
        content: '';
      }
    `,
    className
  );

  const labelSizeClass = useResponsiveProp(size, size => {
    if (size === 'sm') return ['px-1.5', 'text-sm', 'tracking-wider'];
    if (size === 'base') return ['px-2', 'text-base'];
    if (size === 'lg') return ['px-3', 'text-lg'];
  });

  const labelMode = useResponsiveProp(mode, mode => {
    return mode === 'icon' ? ['sr-only', 'px-0!'] : '';
  });

  const labelStyle = tw(
    `relative ${bold ? 'font-bold' : ''} ${uppercase ? 'uppercase' : ''}`,
    labelSizeClass,
    labelMode
  );

  const underLineLabelSize = useResponsiveProp(size, size => {
    if (size === 'sm') return ['text-xs', 'tracking-wider', 'leading-4'];
    if (size === 'base') return ['text-sm', 'leading-5'];
    if (size === 'lg') return ['text-base', 'leading-6'];
  });

  const underLineLabelMode = useResponsiveProp(mode, mode => {
    return mode === 'icon' ? ['sr-only'] : '';
  });

  const underLineLabelStyle = tw(
    `relative ${bold ? 'font-bold' : ''} ${uppercase ? 'uppercase' : ''} px-0`,
    underLineLabelSize,
    underLineLabelMode
  );

  const iconWrapperMode = useResponsiveProp(mode, mode => {
    return mode === 'icon' ? ['mr-0', 'ml-0', 'rtl:ml-0', 'rtl:mr-0'] : '';
  });

  const iconWrapperStyle = tw(
    'relative',
    iconWrapperMode,
    loading ? 'opacity-0' : 'opacity-100'
  );

  const iconSize =
    variant === 'underline' ? 'base' : getIconSizeByActionSize(size);

  const actionChildren = [
    icon ? (
      <div key="icon" data-uds-child="icon" className={iconWrapperStyle}>
        <Icon name={icon} size={iconSize} />
      </div>
    ) : null,
    loading ? (
      <div key="loading" className={tw('absolute', 'text-current')}>
        <DotAnimation
          key="loading"
          on={loading}
          size={size}
          compact={mode === 'icon'}
        />
      </div>
    ) : null,
    srOnlyLabel ? (
      <span key="sr-label" data-uds-child="sr-only-label" className="sr-only">
        {srOnlyLabel}
      </span>
    ) : null,
    <div
      key="label"
      data-uds-child="text-label"
      aria-hidden={srOnlyLabel ? 'true' : 'false'}
      className={variant === 'underline' ? underLineLabelStyle : labelStyle}
    >
      <div className={loading ? 'opacity-0' : 'opacity-100'}>{label}</div>

      {variant === 'underline' ? (
        <div
          key="underline"
          className={tw(
            `h-[${underlineSizeInPx}] absolute -bottom-0.5 w-full z-0`,
            !invert && 'bg-neutral-40',
            !dimmed &&
              'group-hover:bg-neutral-100 group-focus-visible:bg-neutral-100',
            invert && 'bg-neutral-50',
            invert &&
              !dimmed &&
              'group-hover:bg-white group-focus-visible:bg-white',
            className
          )}
        />
      ) : null}
    </div>,
    loading ? (
      <span key="loading-sr-text" className="sr-only">
        {` ${t('loading').toLowerCase()}`}
      </span>
    ) : null,
    dimmed && dimmedReason ? (
      <span
        data-uds-child="dimmed-reason"
        key="dimmed-reason"
        className={tw(
          `absolute bottom-full whitespace-nowrap flex-none mb-2 px-2 py-1 bg-neutral-100 rounded-sm text-white ${
            bold ? 'font-bold' : ''
          } text-xs ${uppercase ? 'uppercase' : ''}`,
          'opacity-0 invisible group-hover:(opacity-100 visible) group-focus-visible:(opacity-100 visible)'
        )}
      >
        {dimmedReason}
      </span>
    ) : null,
  ];

  return {
    className: actionClassName,
    children: actionChildren,
    'aria-disabled': dimmed || undefined,
  };
};
